/**
 * Session portal read projection (issue #321 Milestone 1, ADR 0016).
 *
 *     GET /api/lms/sessions/{sessionId}/workspace
 *
 * THE CLIENT INFERS NO POLICY. Every type below is a mirror of a server
 * derivation, and that is the entire point of the milestone: five surfaces each
 * decided independently whether a meeting was ready, and they disagreed. The
 * fix was not a shared React component — it was moving the decision to one
 * server projection. So:
 *
 *   * `destination_state` and `destination_issue` are read, never recomputed.
 *     Do not add `if (!join_url)` anywhere. A missing URL is `missing_url` and
 *     the server says so.
 *   * `clock_phase` comes from the DATABASE's instant. Do not compare
 *     `starts_at` with `Date.now()`; two readers in different places would
 *     disagree about whether session has started.
 *   * `allowed_actions` is authority already resolved. Do not gate a control on
 *     a role, a capacity, or a status — gate it on membership in this array.
 *   * `primary_action` is the server's answer to "what is the one useful thing
 *     now", so three route shells cannot drift into three answers.
 *
 * If a surface needs a decision this file cannot express, the projection is
 * missing a field. Add it in `backend/app/routers/sessions.py`, not here.
 *
 * `join_url` is present ONLY when the destination is both safe and currently
 * joinable, so an unsafe value is never rendered even by a component that
 * ignores `destination_state`. `recording_url` does not exist in this contract
 * at any level.
 */
import { api } from "@/lib/api/client";
import { commitOptions, type BulkResult } from "@/lib/api/commitSeams";
import type { Modality } from "@/lib/api/courses";
import type { MeetingProvider } from "@/lib/api/tenant";
import type { AttendanceStatus, SessionStatus } from "@/lib/api/attendance";
import type { AssessmentKind, RubricCriterion, SubmissionKind } from "@/lib/api/assignments";

/** Display phase only. `status` remains the institutional state. */
export type ClockPhase = "upcoming" | "scheduled_now" | "status_pending";

export type DestinationState =
  | "not_applicable"
  | "ready"
  | "missing_url"
  | "invalid_configuration";

export type DestinationIssue =
  | "provider_not_enabled"
  | "unsafe_url"
  | "embedded_credentials"
  | "missing_meeting_path"
  | "provider_host_mismatch"
  | "provider_not_remote"
  | "remote_provider_for_in_person";

export type EffectiveSource = "course_default" | "session_override";

export type DestinationResolution =
  | "join_ready"
  | "in_person"
  | "needs_destination"
  | "needs_repair"
  | "cancelled_no_destination"
  | "none";

/** Exact action identifiers. The client renders these and decides none of them. */
export type SessionAction =
  | "join_meeting"
  | "set_meeting_link"
  | "fix_meeting_link"
  | "create_google_meet"
  | "replace_google_meet"
  | "create_zoom_meeting"
  | "replace_zoom_meeting"
  | "clear_session_override"
  | "create_session_plan"
  | "edit_session_plan"
  | "take_attendance"
  | "complete_attendance"
  | "close_session"
  | "update_roll"
  | "publish_session_plan"
  | "review_preparation"
  | "view_location"
  | "view_course"
  // Native publication is reviewed inside Prepare; Classroom is separate.
  | "publish_session_resource"
  | "manage_session_coursework"
  | "open_session_activity"
  | "cancel_session"
  | "move_session"
  | "reinstate_session";
// Schedule changes are quiet acts, never ranked as the session's primary act.

export type ViewerCapacity = "teaching" | "learning";

export interface WorkspaceSession {
  id: string;
  course_id: string;
  session_date: string;
  session_no: number | null;
  label: string | null;
  status: SessionStatus;
  starts_at: string;
  ends_at: string;
  /** The optimistic-concurrency precondition sent back on a destination save. */
  updated_at: string;
  clock_phase: ClockPhase;
  /** Course-local, from `lms_course.timezone` — never the browser's zone. */
  is_today: boolean;
  latest_change?: SessionPublicChange | null;
}

export interface SessionPublicChange {
  kind: "cancel" | "move" | "reinstate";
  note: string;
  from_date: string | null;
}
export type SessionChangeKind = SessionPublicChange["kind"];
/** A batch cancellation: each session cancelled or refused on its own (W1), and the one notice. */
export interface SessionCancelResult extends BulkResult {
  kind: "cancel";
  delivery: SessionChangeReceipt["delivery"];
}

export interface SessionChangeReceipt {
  kind: SessionChangeKind;
  session_ids: string[];
  change_ids: string[];
  announcement_id: string | null;
  /** Why no one was told, committed with the change (CC2). */
  withheld?: SessionNoticeWithheld | null;
  delivery: { state: "off" | "sent" | "partial" | "none_eligible" | "failed" | "unconfirmed" | SessionNoticeWithheld["state"]; message: string };
}

export interface SessionNoticeWithheld { state: "unpublished" | "no_learners"; message: string }

/** The committed reason no one was told, read from a Check receipt; null when a notice posted or none was asked. */
export function withheldNotice(value: unknown): string | null {
  if (typeof value !== "object" || value === null) return null;
  const { state, message } = value as Record<string, unknown>;
  return (state === "unpublished" || state === "no_learners") && typeof message === "string" && message.trim() ? message : null;
}

export interface WorkspaceCourse {
  id: string;
  code: string;
  title: string;
  title_ar: string | null;
  /** IANA zone. Every rendered instant is formatted in this, not the browser's. */
  timezone: string;
  meeting_time: string | null;
  term_open: boolean;
  /** The term's name, or null for a course with no term. Always sent; optional here for fixtures written before it. */
  term_name?: string | null;
}

/** Present only for the teaching team; a learner's payload omits it entirely. */
export interface DestinationProvenance {
  scope: EffectiveSource;
  action: "set" | "replace" | "clear" | "inherit";
  organizer_ownership: "tenant_workspace" | "external_or_personal" | "unknown";
  evidence_basis: "staff_assertion" | "provider_verified" | "legacy_unknown";
  destination_host: string | null;
  occurred_at: string;
}

export interface WorkspaceDestination {
  modality: Modality;
  meeting_provider: MeetingProvider | null;
  location: string | null;
  destination_state: DestinationState;
  /** Staff diagnostics and legacy flag-off responses only. */
  destination_issue?: DestinationIssue | null;
  /** Legacy flag-off responses and assigned teaching responses carry source.
   * D4 learner responses omit it structurally. */
  effective_source?: EffectiveSource;
  join_url: string | null;
  join_label: string | null;
  /** Present together only when the per-tenant D4 projection is enabled. */
  resolution?: DestinationResolution;
  resolution_action?: SessionAction | null;
  /** Staff-only editor context. Absent for a learner, by construction. */
  course_default?: {
    modality: Modality;
    meeting_provider: MeetingProvider | null;
    meeting_url: string | null;
    location: string | null;
  };
  overrides?: {
    modality: Modality | null;
    meeting_provider: MeetingProvider | null;
    meeting_url: string | null;
    location: string | null;
  };
  enabled_providers?: MeetingProvider[];
  provenance?: DestinationProvenance | null;
  /** Present only for the assigned teaching team when Mufid's managed Meet
   * release gate is enabled. Learners never receive provider operation state. */
  managed_google_meet?: {
    state: "none" | "pending" | "ready" | "failed" | "unknown";
    meeting_uri: string | null;
    failure_classification: string | null;
    created_at: string | null;
    completed_at: string | null;
  };
  /** Present only for the assigned teaching team when Mufid's managed Zoom
   * release gate is enabled. Learners never receive provider operation state. */
  managed_zoom?: {
    state: "none" | "pending" | "ready" | "failed" | "unknown";
    /** The lifecycle operation, not client-side control authority. */
    operation: "create" | "replace";
    meeting_uri: string | null;
    /** Present for a ready binding only. IMAM never changes Zoom automatically. */
    schedule_state?: "current" | "drifted" | null;
    /** Boolean-only cross-course conflict signal. Never present for learners. */
    shared_host_conflict: boolean;
    /** Staff-only signal that the effective safe destination is manual. */
    manual_destination: boolean;
    failure_classification: string | null;
    created_at: string | null;
    completed_at: string | null;
  };
}

export interface WorkspaceViewer {
  capacities: ViewerCapacity[];
  allowed_actions: SessionAction[];
  primary_action: SessionAction | null;
  /** A learner's ranked act `open_session_activity` opens this work: the server's pick. */
  primary_work_id?: string;
}

/**
 * Coursework projection (issue #391 Milestone 3, ADR 0018).
 *
 * `materials`/`assignments` are present on a `WorkspaceUnit` if and only if
 * `SessionWorkspace["plan"]["coursework_state"] === "ok"` — that key's absence
 * IS the flag-off shape, so there is no separate boolean to read here.
 * `coursework_state === "unavailable"` means the read failed and neither array
 * is present on the unit; it does not mean "both arrays are empty".
 *
 * Every field below is a straight mirror of `docs/design/321-milestone-3-
 * session-scoped-coursework.md`'s wire contract. Teaching and learner variants
 * are separate interfaces, not one interface with optional fields, because the
 * contract is a closed, EXACT key set per role (Milestone 3 design acceptance
 * criterion 3) — an optional field would let a learner-shaped object silently
 * satisfy the teaching type and hide a projection leak. Narrow a `SessionMaterial`
 * / `SessionAssignment` union member with `"placement_id" in item` /
 * `"coverage" in item` (teaching) or their absence (learner).
 */
export type CourseworkState = "ok" | "unavailable";

/** Closed vocabulary. The client renders a control iff its id is present. */
export type MaterialAction =
  | "open_material"
  | "publish_placement"
  | "manage_in_course_materials";

interface SessionMaterialBase {
  id: string;
  title: string;
  material_type: string;
  file_url: string | null;
  /** What the resource IS, never an identifier — see the module docstring. */
  google_drive_mime_type: string | null;
  sort_order: number;
  actions: MaterialAction[];
}

export interface TeachingSessionMaterial extends SessionMaterialBase {
  publication_state: "published" | "draft" | "unplaced";
  learner_visible: boolean;
  unit_published: boolean;
  /** Non-null only when `publication_state === "draft"` names one candidate. */
  placement_id: string | null;
  placement_revision: number | null;
}

/**
 * Constant by construction, per the contract: a learner is never handed a
 * draft, so `publication_state`/`learner_visible`/`unit_published` are fixed
 * literals rather than the wider teaching unions. `placement_id`/
 * `placement_revision` do not exist on this shape at all.
 */
export interface LearnerSessionMaterial extends SessionMaterialBase {
  publication_state: "published";
  learner_visible: true;
  unit_published: true;
}

export type SessionMaterial = TeachingSessionMaterial | LearnerSessionMaterial;

export type AssignmentTeachingAction =
  | "publish_assignment"
  | "correct_terms"
  | "review_attempts"
  | "edit_terms"
  | "remove_assignment"
  | "close_assignment";
export type AssignmentLearnerAction = "open_activity";

/** Enrollment-based, never attendance-based — the projection's own rule. */
export interface AssignmentCoverage {
  enrolled_count: number;
  submitted_count: number;
  awaiting_review_count: number;
}

interface SessionAssignmentBase {
  id: string;
  title: string;
  points: number | null;
  due_at: string | null;
}

export interface TeachingSessionAssignment extends SessionAssignmentBase {
  /** The terms the session tile's own editor works on. */
  course_id: string;
  unit_id: string | null;
  instructions: string | null;
  /** The server can also answer "none" (`ExpectedDeliverable`); narrow until the box that reads it is widened. */
  expected_deliverable: SubmissionKind | null;
  /** How the work is judged; the tile and Review read their words from it.
   *  Always sent; optional here for surfaces and specs written before it. */
  assessment_kind?: AssessmentKind;
  external_url?: string | null;
  rubric?: RubricCriterion[] | null;
  updated_at: string;
  status: "draft" | "published" | "closed";
  learner_visible: boolean;
  unit_published: boolean;
  grade_item_id: string | null;
  /** Null exactly when `grade_item_id` is null. */
  grade_item_label: string | null;
  coverage: AssignmentCoverage;
  actions: AssignmentTeachingAction[];
}

/** The five values a learner's own attempt state can hold. */
export type LearnerAssignmentState =
  | "not_submitted"
  | "submitted"
  | "returned"
  | "resubmitted"
  | "graded";

export interface LearnerSessionAssignment extends SessionAssignmentBase {
  /** The database's word: the due time has passed. Never compared with the browser's clock. */
  past_due: boolean;
  /** What the learner hands in, or null for work that takes any submission. The server also answers "none"
   *  (`ExpectedDeliverable`) for a quiz, in-class work or a plain mark of done; narrow until the box that reads it is widened. */
  expected_deliverable: SubmissionKind | null;
  assessment_kind?: AssessmentKind;
  /** The quiz link, external quiz only. */
  external_url?: string | null;
  /** The grade line this work counts toward ("Quizzes"), or null with no line. Always sent; optional here for fixtures written before it. */
  grade_item_label?: string | null;
  /** The brief, as the assignment states it. The page reads its first paragraph until the first hand-in. */
  instructions: string | null;
  status: "published" | "closed";
  learner_visible: true;
  unit_published: true;
  state: LearnerAssignmentState;
  attempt_count: number;
  latest_attempt_at: string | null;
  /** The latest RELEASED feedback instant only. A withheld judgement never moves
   *  this and never moves `state`: the portal does not say a judgement exists
   *  but is hidden. */
  latest_feedback_at: string | null;
  resubmission_open: boolean;
  actions: AssignmentLearnerAction[];
}

export type SessionAssignment = TeachingSessionAssignment | LearnerSessionAssignment;

export interface WorkspaceUnit {
  id: string;
  position: number;
  topic: string;
  learning_objectives: string[];
  preparation_guidance: string | null;
  recap: string | null;
  follow_up: string | null;
  status: "draft" | "published";
  revision: number;
  /** Present only when `SessionWorkspace["plan"]["coursework_state"] === "ok"`. */
  materials?: SessionMaterial[];
  /** Present only when `SessionWorkspace["plan"]["coursework_state"] === "ok"`. */
  assignments?: SessionAssignment[];
}

/** The teaching team's roster summary. */
export interface WorkspaceAttendanceSummary {
  kind: "summary";
  roster_count: number;
  recorded_count: number;
  by_status: Record<AttendanceStatus, number>;
}

/** A learner's own mark. `status: null` is "not marked", NEVER "absent". */
export interface WorkspaceOwnAttendance {
  kind: "own";
  status: AttendanceStatus | null;
  camera_on: boolean | null;
}

/** Closed, course-authorized navigation. Position names order, never completion. */
export interface SessionNeighbour {
  row_id: string;
  id: string;
  /** The stored class number, the one `classTitle` names a class by. */
  session_no: number | null;
  label: string | null;
  starts_at: string;
  ends_at: string;
  timezone: string;
  institutional_status: "scheduled" | "held" | "cancelled";
  latest_change?: SessionPublicChange | null;
  destination: { id: "open_session"; href: string; label: "Open session" };
}
export type OptionalSessionRead<T> =
  | { state: "ready"; value: T }
  | { state: "unavailable" };
/** One Follow up fact: its own read, its own state. An unavailable read is never a zero. */
export type FollowUpRead<T> =
  | { state: "ready"; value: T }
  | { state: "empty" }
  | { state: "unavailable" };
export type FollowUpActId =
  | "join_now"
  | "close_last_session"
  | "prepare_this_session"
  | "publish_recap"
  | "review_attempts"
  | "revise"
  | "close_older_session"
  | "none";
export interface FollowUpSession {
  id: string;
  session_no: number | null;
  label: string | null;
  starts_at: string;
  ends_at: string;
  timezone: string;
  /** A move the schedule carries, as every neighbour carries it. */
  latest_change?: SessionPublicChange | null;
  destination: { id: "open_session"; href: string; label: "Open session" };
}
interface FollowUpLastBase extends FollowUpSession {
  /** Held, or ended without being held. A cancelled session is never the last. */
  status: "held" | "ended";
}
/** The teaching team's facts about the last session. A learner's payload carries none of them. */
export interface FollowUpLastTeaching extends FollowUpLastBase {
  roll: FollowUpRead<{ recorded: boolean; unrecorded_count: number | null }>;
  recap: FollowUpRead<{ published: boolean; waiting: boolean }>;
  review: FollowUpRead<{ count: number; destination: { id: "review_attempt"; href: string; label: "Review attempt" } }>;
}
/** The learner's own facts. */
export interface FollowUpLastLearning extends FollowUpLastBase {
  attendance: FollowUpRead<{ status: AttendanceStatus | null }>;
  recap: FollowUpRead<{ available: boolean }>;
  work: FollowUpRead<{
    state: "revision_open" | "to_hand_in" | "waiting_feedback";
    count: number;
    destination: { id: "open_coursework"; href: string; label: "Open coursework" };
  }>;
}
export interface FollowUp {
  /** Ranked by the server. The client never re-ranks. */
  act: { id: FollowUpActId; session_id?: string };
  last_session: FollowUpRead<FollowUpLastTeaching | FollowUpLastLearning>;
  earlier_sessions: FollowUpRead<{ count: number; sessions: FollowUpSession[] }>;
  next_scheduled: FollowUpRead<FollowUpSession & {
    plan: FollowUpRead<{ word: "none" | "not_published" | "published" } | { first_line: string | null }>;
  }>;
}
/** The foot reads forward on every page: the nearest earlier session that is not cancelled (held, ended unheld, or yet to meet), the next scheduled, the cancelled between. */
export interface SessionFoot {
  previous: FollowUpRead<FollowUpSession & { status: "held" | "ended" | "scheduled" }>;
  next_scheduled: FollowUp["next_scheduled"];
  cancelled: FollowUpRead<FollowUpSession[]>;
}
/** The acts that put the page's one filled act in Follow up. Every other box on the page stays quiet. */
export const FOLLOW_UP_HOLDS = new Set(["close_last_session", "publish_recap", "review_attempts", "revise", "close_older_session"]);

export function followUpHoldsAct(followUp: FollowUp | undefined) {
  return !!followUp && FOLLOW_UP_HOLDS.has(followUp.act.id) && followUpHasBox(followUp);
}

/** Follow up has a box when a last or earlier session left something to read, or a read failed. */
export function followUpHasBox(followUp: FollowUp | undefined) {
  if (!followUp) return false;
  const { last_session: last, earlier_sessions: earlier } = followUp;
  return last.state !== "empty" || earlier.state !== "empty";
}

export interface SessionContinuation {
  foot?: SessionFoot;
  /** Present only on the one session Rule C8 resolves for the course. */
  follow_up?: FollowUp;
  neighbours: OptionalSessionRead<{ previous_session: SessionNeighbour | null; next_session: SessionNeighbour | null }>;
  outstanding: OptionalSessionRead<{ count: number; destination: { id: "open_coursework"; href: string; label: "Open coursework" } }> | { state: "empty" };
}

/** One box's place in the page's ranking read; `rank` is null unless its source was read. */
export interface SessionRankingCandidate {
  id: string;
  rank: number | null;
  eligible: boolean;
  blocked: boolean;
  read: "ready" | "empty" | "unavailable";
}

/**
 * P2: the page's one ranking read, by box id (follow-up, prepare, teach,
 * close, review; get-ready, hand-in). Only a ready read ranks anything.
 */
export type SessionRanking =
  | { state: "ready"; value: { candidates: SessionRankingCandidate[]; lead: string | null } }
  | { state: "empty" | "unavailable" };

export interface SessionWorkspace {
  session: WorkspaceSession;
  continuation?: SessionContinuation;
  ranking: SessionRanking;
  sequence?: OptionalSessionRead<{ position: number; total: number; sessions: SessionNeighbour[] }>;
  course: WorkspaceCourse;
  destination: WorkspaceDestination;
  viewer: WorkspaceViewer;
  /** Teaching-only effects. Publication remains an independently chosen gate. */
  close_effects?: { records_roll: boolean; holds_session: boolean; reveals_published_recap: boolean };
  plan: {
    /** The server places a pending plan publication with its changed fields. */
    publication_task?: "prepare" | "close" | null;
    /** One session, one plan (ADR 0033 amendment 2026-10-08): the course unit
     *  attached to this session, or null when the session has no plan. A learner
     *  receives it only once published. */
    unit: WorkspaceUnit | null;
    /**
     * Absent entirely when the coursework flag is off — the response key set
     * is then byte-for-byte Milestone 1's. `"ok"` means the plan unit's
     * `materials`/`assignments` are populated (possibly empty arrays).
     * `"unavailable"` means the coursework read failed and the unit carries
     * neither array; the rest of the workspace stays usable.
     */
    coursework_state?: CourseworkState;
  };
  attendance: WorkspaceAttendanceSummary | WorkspaceOwnAttendance;
}

export const sessionPortalApi = {
  change: (courseId: string, sessionId: string, kind: SessionChangeKind, body: {
    note: string; tell_students: boolean; expected_updated_at: string;
    session_date?: string; start_time?: string | null; duration_minutes?: number;
  }, key: string) => api.post<SessionChangeReceipt>(`/courses/${courseId}/sessions/${sessionId}/${kind}`, body, commitOptions(key)),
  workspace: (sessionId: string) =>
    api.get<SessionWorkspace>(`/sessions/${sessionId}/workspace`),
  googleMeetAuthorization: (
    sessionId: string,
    body: { code_challenge: string; replace: boolean },
  ) => api.post<{ authorization_url: string; state: string; replace: boolean }>(
    `/sessions/${sessionId}/google-meet/authorization`, body,
  ),
  completeGoogleMeetAuthorization: (
    sessionId: string,
    body: { state: string; code: string; code_verifier: string; replace: boolean },
  ) => api.post<{ state: "pending" | "ready"; meeting_uri: string | null; action?: string }>(
    `/sessions/${sessionId}/google-meet/complete`, body,
  ),
  createZoomMeeting: (
    sessionId: string,
    body: { replace: boolean },
  ) => api.post<{ state: "pending" | "ready"; meeting_uri: string | null; action?: "bind" | "replace" }>(
    `/sessions/${sessionId}/zoom/meetings`, body,
  ),
};
